/**
 * Client HTTP typé pour WebForge Survey.
 *
 * Deux clients distincts :
 * - `publicApi` : aucun header d'authentification ; sert le parcours répondant.
 * - `authedApi` : Bearer JWT ; sert l'app créateur (Phase 2 minimal).
 *
 * RGPD :
 * - Aucun fingerprinting (no `withCredentials` global, pas de tracking).
 * - Aucun custom header inutile susceptible de fuiter dans des intermédiaires.
 * - Le token JWT est conservé en mémoire + localStorage (réauthent en lecture
 *   au démarrage). En mode `anonymous`, **aucun** token n'est jamais émis.
 */
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosError } from 'axios';
import type {
  Answers,
  EnginePayload,
  Identity,
  SurveyDefinition,
} from '@webforge-survey/shared';

const API_BASE = '/api';

/** Création d'une instance axios avec en-têtes communs. */
function createClient(): AxiosInstance {
  return axios.create({
    baseURL: API_BASE,
    timeout: 15_000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
}

export const publicApi: AxiosInstance = createClient();

/** Instance authentifiée — JWT injecté via interceptor. */
export const authedApi: AxiosInstance = createClient();

const TOKEN_KEY = 'wfs_jwt';

export function getStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* localStorage indisponible — login forcé à chaque session */
  }
}

// E-mail du créateur connecté (mémorisé au login pour l'affichage topbar).
// Ce n'est PAS un secret ; aucune donnée répondant. Évite un endpoint /me.
const EMAIL_KEY = 'wfs_email';

export function getStoredEmail(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

export function setStoredEmail(email: string | null): void {
  try {
    if (email === null) window.localStorage.removeItem(EMAIL_KEY);
    else window.localStorage.setItem(EMAIL_KEY, email);
  } catch {
    /* ignore */
  }
}

authedApi.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

authedApi.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    // 401 sur une route authentifiée = token invalide / expiré.
    // On déconnecte + redirige vers /login (sauf si on y est déjà).
    if (error.response?.status === 401) {
      setStoredToken(null);
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        // Notifier les écouteurs (RequireAuth lit via storage event).
        window.dispatchEvent(new StorageEvent('storage', { key: TOKEN_KEY }));
        // Redirection douce — laisse le temps à React de désabonner.
        window.location.assign(`/login?from=${encodeURIComponent(window.location.pathname)}`);
      }
    }
    return Promise.reject(error);
  },
);

// ---------- Public endpoints (parcours répondant) ----------

/** Réponse de POST /s/:slug/session — contient le token opaque de reprise. */
export interface CreateSessionResponse {
  /** Identifiant interne de la session — sert d'idempotency-key au submit. */
  sessionId: string;
  /** Token opaque hex 64 char — sert d'authentifiant pour autosave/submit/get. */
  resumeToken: string;
}

export interface SessionSummary {
  /** Identifiant opaque ; jamais corrélable à un répondant. */
  sessionId: string;
  /** Slug + version pour pinning. */
  surveySlug?: string;
  definitionVersion: number;
  answers: Answers;
  /** Identité non-stockée en local ; jamais retournée si mode anonymous. */
  identity?: Identity;
  /** Timestamp ISO. */
  updatedAt?: string;
  /** Le serveur indique si la session est encore en cours / soumise. */
  status: 'in_progress' | 'submitted' | 'expired' | 'completed';
}

export interface AutosaveResponse {
  sessionId: string;
  updatedAt: string;
}

export interface SubmitResponse {
  ok: true;
  /** Confirmation opaque ; en mode anonymous, jamais d'identifiant ré-identifiant. */
  acknowledgmentId: string;
}

// Backend expose les routes publiques sous /s/:slug (API_SPEC §7).
// Le frontend appelle /s/:slug, /s/:slug/session, etc. — pas de /public/surveys/...

/** Récupère la définition d'enquête publique (slug = path). */
export async function getSurveyDefinition(slug: string): Promise<SurveyDefinition> {
  const { data } = await publicApi.get<SurveyDefinition>(
    `/s/${encodeURIComponent(slug)}`,
  );
  return data;
}

// ===========================================================================
// WIZARD (RESPONDENT_WIZARD_SPEC v1.3) — endpoints respondent supplémentaires
// ===========================================================================

/**
 * Autosave wizard atomique MERGE (PATCH). Distinct de l'autosave PUT existant
 * (qui remplace tout) — sert le client wizard (1 question/écran).
 */
export async function patchWizardAnswers(
  slug: string,
  token: string,
  partial: Record<string, unknown>,
  idempotencyKey?: string,
): Promise<{ sessionId: string; mergedKeysCount: number; idempotentReplay: boolean }> {
  const { data } = await publicApi.patch<{
    sessionId: string;
    mergedKeysCount: number;
    idempotentReplay: boolean;
  }>(
    `/s/${encodeURIComponent(slug)}/session/${encodeURIComponent(token)}/answers`,
    { partial, ...(idempotencyKey ? { idempotencyKey } : {}) },
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
  );
  return data;
}

/** Reprise multi-appareil par code (timing-safe côté serveur + rotate immédiat). */
export async function resumeWizardByCode(
  slug: string,
  code: string,
): Promise<{
  sessionId: string;
  resumeToken: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  definitionVersion: number;
  answers: Record<string, unknown>;
}> {
  const { data } = await publicApi.post(
    `/s/${encodeURIComponent(slug)}/session/resume`,
    { code },
  );
  return data;
}

/** Droit à l'effacement art. 17 — exige body confirm:"EFFACER". */
export async function eraseWizardSession(
  slug: string,
  token: string,
): Promise<{ erased: true }> {
  const { data } = await publicApi.post<{ erased: true }>(
    `/s/${encodeURIComponent(slug)}/session/${encodeURIComponent(token)}/erase`,
    { confirm: 'EFFACER' },
  );
  return data;
}

/**
 * GET du code de reprise courant (cookie-driven). À la 3ᵉ exposition du jour,
 * le serveur rotate transparent (transparent côté UI — re-Set-Cookie).
 * Header X-Requested-With explicite (R-16 SENTINEL CSRF double-check).
 */
export async function getCurrentResumeCode(
  slug: string,
): Promise<{ code: string; rotated: boolean; exposureCount: number }> {
  const { data } = await publicApi.get(
    `/s/${encodeURIComponent(slug)}/session/current/code`,
    { headers: { 'X-Requested-With': 'XMLHttpRequest' } },
  );
  return data;
}

/** Force la rotation du code (« Régénérer mon code »). */
export async function regenerateResumeCode(slug: string): Promise<{ code: string }> {
  const { data } = await publicApi.post(
    `/s/${encodeURIComponent(slug)}/session/current/regenerate-code`,
    {},
    { headers: { 'X-Requested-With': 'XMLHttpRequest' } },
  );
  return data;
}

/** Crée une nouvelle session (consentement accordé). Retourne sessionId + resumeToken. */
export async function createSession(slug: string): Promise<CreateSessionResponse> {
  const { data } = await publicApi.post<CreateSessionResponse>(
    `/s/${encodeURIComponent(slug)}/session`,
  );
  return data;
}

/** Récupère une session existante via token opaque. */
export async function getSession(
  slug: string,
  resumeToken: string,
): Promise<SessionSummary | null> {
  try {
    const { data } = await publicApi.get<SessionSummary>(
      `/s/${encodeURIComponent(slug)}/session/${encodeURIComponent(resumeToken)}`,
    );
    return data;
  } catch (err) {
    const ax = err as AxiosError;
    if (ax.response?.status === 404 || ax.response?.status === 410) return null;
    throw err;
  }
}

/**
 * Autosave — PUT partiel. Aucune identité côté autosave (toujours stripped).
 * Le `resumeToken` (opaque hex) authentifie la session — il transite dans l'URL.
 */
export async function autosaveSession(
  slug: string,
  resumeToken: string,
  payload: { answers: Answers },
): Promise<AutosaveResponse> {
  const { data } = await publicApi.put<AutosaveResponse>(
    `/s/${encodeURIComponent(slug)}/session/${encodeURIComponent(resumeToken)}`,
    payload,
  );
  return data;
}

/**
 * Soumission finale : payload moteur (answers + identity séparée si applicable).
 * Backend exige :
 *  - `x-resume-token` (header) : authentifie la session (opaque hex).
 *  - `Idempotency-Key` (header) : sessionId — empêche les double-submits.
 */
export async function submitSession(
  slug: string,
  sessionId: string,
  resumeToken: string,
  payload: EnginePayload,
): Promise<SubmitResponse> {
  const { data } = await publicApi.post<SubmitResponse>(
    `/s/${encodeURIComponent(slug)}/submit`,
    payload,
    {
      headers: {
        'Idempotency-Key': sessionId,
        'x-resume-token': resumeToken,
      },
    },
  );
  return data;
}

/**
 * Opt-in identité (mode optional) — endpoint séparé pour garantir la non
 * ré-attachabilité : si le répondant l'appelle, c'est qu'il a coché. Sinon,
 * le serveur n'a jamais eu d'identité.
 */
export async function optInIdentity(
  slug: string,
  resumeToken: string,
  identity: Identity,
): Promise<{ ok: true }> {
  // `consent: true` explicite (l'utilisateur a coché l'opt-in, jamais pré-coché) —
  // exigé par le backend (RGPD_PLAN §2.2).
  const { data } = await publicApi.post<{ ok: true }>(
    `/s/${encodeURIComponent(slug)}/identity/opt-in`,
    { identity, consent: true },
    { headers: { 'x-resume-token': resumeToken } },
  );
  return data;
}

// ---------- Authenticated endpoints (créateur — Phase 2 minimal) ----------

export interface AuthLoginResponse {
  accessToken: string;
}

export async function login(email: string, password: string): Promise<AuthLoginResponse> {
  const { data } = await publicApi.post<AuthLoginResponse>('/auth/login', {
    email,
    password,
  }, { withCredentials: true });
  setStoredToken(data.accessToken);
  setStoredEmail(email.trim().toLowerCase());
  return data;
}

export function logout(): void {
  setStoredToken(null);
  setStoredEmail(null);
}

export interface Workspace {
  id: string;
  name: string;
  role: 'owner' | 'editor' | 'analyst' | 'viewer';
}

export async function listWorkspaces(): Promise<Workspace[]> {
  // Backend renvoie `{ workspaces: [...] }` (API_SPEC §3). On dépaquete pour
  // garder une API frontend simple.
  const { data } = await authedApi.get<{ workspaces: Workspace[] } | Workspace[]>('/workspaces');
  return Array.isArray(data) ? data : (data.workspaces ?? []);
}

// ---------- Paramètres workspace (Phase B8) ----------

/**
 * Version courante du DPA proposée par la plateforme (alignée sur le seed).
 * Si le workspace a accepté une version antérieure, l'UI propose une re-signature.
 */
export const CURRENT_DPA_VERSION = '1.0';

/**
 * Entité responsable du traitement (RGPD art. 4 §7). Forme alignée sur ce qui
 * est affiché au répondant pour transparence (cf. ControllerEntityDto backend).
 * Aucune PII répondant ici — uniquement le contrôleur (institution/chercheur).
 */
export interface ControllerEntity {
  /** Raison sociale / nom de l'entité responsable. */
  name?: string;
  /** Contact du responsable (e-mail ou adresse postale). */
  contact?: string;
  /** Nom du délégué à la protection des données. */
  dpoName?: string;
  /** E-mail du DPO (point de contact des personnes concernées). */
  dpoEmail?: string;
}

/** Vue complète d'un workspace (paramètres RGPD inclus) — owner only en écriture. */
export interface WorkspaceSettings {
  id: string;
  name: string;
  role: WorkspaceRole;
  controllerEntity: ControllerEntity | null;
  dpaVersion: string;
  dpaAcceptedAt: string;
  retentionDaysCap: number;
  createdAt: string;
}

export async function getWorkspace(workspaceId: string): Promise<WorkspaceSettings> {
  const { data } = await authedApi.get<{ workspace: WorkspaceSettings }>(
    `/workspaces/${encodeURIComponent(workspaceId)}`,
  );
  return data.workspace;
}

export async function updateWorkspace(
  workspaceId: string,
  patch: { name?: string; controllerEntity?: ControllerEntity; retentionDaysCap?: number },
): Promise<WorkspaceSettings> {
  const { data } = await authedApi.patch<{ workspace: WorkspaceSettings }>(
    `/workspaces/${encodeURIComponent(workspaceId)}`,
    patch,
  );
  return data.workspace;
}

/** Re-signature d'une nouvelle version de DPA (owner). */
export async function acceptDpa(
  workspaceId: string,
  dpaVersion: string,
): Promise<WorkspaceSettings> {
  const { data } = await authedApi.post<{ workspace: WorkspaceSettings }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/dpa-accept`,
    { dpaVersion, dpaAccepted: true },
  );
  return data.workspace;
}

// ---------- Membres, invitations, partage (Phase B — collaboration) ----------

export type WorkspaceRole = 'owner' | 'editor' | 'analyst' | 'viewer';

export interface Member {
  membershipId: string;
  creatorId: string;
  email: string;
  role: WorkspaceRole;
  joinedAt: string;
}

export async function listMembers(workspaceId: string): Promise<Member[]> {
  const { data } = await authedApi.get<{ members: Member[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/members`,
  );
  return data.members ?? [];
}

export async function updateMemberRole(
  workspaceId: string,
  membershipId: string,
  role: WorkspaceRole,
): Promise<Member> {
  const { data } = await authedApi.patch<{ member: Member }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(membershipId)}`,
    { role },
  );
  return data.member;
}

export async function removeMember(workspaceId: string, membershipId: string): Promise<void> {
  await authedApi.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(membershipId)}`,
  );
}

export interface Invitation {
  id: string;
  workspaceName: string;
  email: string;
  role: WorkspaceRole;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'expired';
}

export async function createInvitation(
  workspaceId: string,
  email: string,
  role: WorkspaceRole,
): Promise<{ token: string; invitationId: string; acceptUrl: string; emailSent: boolean }> {
  const { data } = await authedApi.post<{ token: string; invitationId: string; acceptUrl: string; emailSent: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations`,
    { email, role },
  );
  return data;
}

export async function listInvitations(workspaceId: string): Promise<Invitation[]> {
  const { data } = await authedApi.get<{ invitations: Invitation[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations`,
  );
  return data.invitations ?? [];
}

export async function revokeInvitation(workspaceId: string, invitationId: string): Promise<void> {
  await authedApi.delete(
    `/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}`,
  );
}

/** Description publique d'une invitation (par token, sans auth). */
export async function describeInvitation(token: string): Promise<Invitation> {
  const { data } = await publicApi.get<{ invitation: Invitation }>(
    `/invitations/${encodeURIComponent(token)}`,
  );
  return data.invitation;
}

/** Accepte une invitation. `password` requis seulement si le compte n'existe pas encore. */
export async function acceptInvitation(
  token: string,
  password?: string,
): Promise<{ creatorId: string; workspaceId: string }> {
  const { data } = await publicApi.post<{ creatorId: string; workspaceId: string }>(
    `/invitations/${encodeURIComponent(token)}/accept`,
    password ? { password } : {},
  );
  return data;
}

export interface SurveyAccessEntry {
  creatorId: string;
  email: string;
  role: WorkspaceRole;
}

export async function listSurveyAccess(workspaceId: string, surveyId: string): Promise<SurveyAccessEntry[]> {
  const { data } = await authedApi.get<SurveyAccessEntry[]>(
    `/surveys/${encodeURIComponent(surveyId)}/access`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

export async function setSurveyAccess(
  workspaceId: string,
  surveyId: string,
  memberId: string,
  role: WorkspaceRole,
): Promise<void> {
  await authedApi.post(
    `/surveys/${encodeURIComponent(surveyId)}/access`,
    { memberId, role },
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
}

export async function removeSurveyAccess(
  workspaceId: string,
  surveyId: string,
  creatorId: string,
): Promise<void> {
  await authedApi.delete(
    `/surveys/${encodeURIComponent(surveyId)}/access/${encodeURIComponent(creatorId)}`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
}

export interface SurveySummary {
  id: string;
  slug: string;
  title: string;
  definitionVersion: number;
  identityMode: 'anonymous' | 'optional' | 'required';
  status: 'draft' | 'published' | 'closed' | 'archived';
  responsesCount: number;
  /** ISO — date de création de l'enquête. */
  createdAt?: string;
  /** ISO date du jour de la dernière réponse, ou null si aucune. */
  lastResponseAt?: string | null;
}

export async function listSurveys(workspaceId: string): Promise<SurveySummary[]> {
  // Backend : GET /surveys, scope workspace via header X-Workspace-Id (TenantGuard).
  const { data } = await authedApi.get<{ surveys: SurveySummary[] } | SurveySummary[]>(
    '/surveys',
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return Array.isArray(data) ? data : (data.surveys ?? []);
}

export async function publishSurvey(
  workspaceId: string,
  surveyId: string,
): Promise<{ slug: string; definitionVersion: number }> {
  const { data } = await authedApi.post<{ slug: string; definitionVersion: number }>(
    `/surveys/${encodeURIComponent(surveyId)}/versions`,
    {},
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

// ---------- Création / édition d'enquêtes (Phase 3 — éditeur) ---------------

export interface CreateSurveyInput {
  title: string;
  slug: string;
  identityMode: 'anonymous' | 'optional' | 'required';
  identitySchema?: Record<string, unknown>;
  retentionDays?: number;
}

export interface SurveyFull extends SurveySummary {
  workspaceId: string;
  currentVersionId: string | null;
  retentionDays: number | null;
  /** Backend renvoie `identitySchemaJson` ; on normalise vers `identitySchema`. */
  identitySchema?: Record<string, unknown> | null;
  identitySchemaJson?: Record<string, unknown> | null;
}

/** Normalise la forme backend : expose `identitySchema` depuis `identitySchemaJson`. */
function normalizeSurvey(s: SurveyFull): SurveyFull {
  return { ...s, identitySchema: s.identitySchema ?? s.identitySchemaJson ?? null };
}

export async function createSurvey(
  workspaceId: string,
  input: CreateSurveyInput,
): Promise<SurveyFull> {
  const { data } = await authedApi.post<SurveyFull>('/surveys', input, {
    headers: { 'X-Workspace-Id': workspaceId },
  });
  return normalizeSurvey(data);
}

export async function getSurvey(workspaceId: string, surveyId: string): Promise<SurveyFull> {
  const { data } = await authedApi.get<SurveyFull>(
    `/surveys/${encodeURIComponent(surveyId)}`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return normalizeSurvey(data);
}

/**
 * Charge la définition COURANTE d'une enquête pour l'ÉDITEUR (endpoint
 * authentifié, scope workspace). Contrairement à {@link getSurveyDefinition}
 * (public, réservé aux enquêtes PUBLIÉES), celui-ci renvoie le contenu quel que
 * soit le statut — y compris « fermée » ou « brouillon ». Indispensable pour
 * éviter d'afficher « Blocs (0) » dès qu'une enquête n'est plus publiée.
 * Retourne `null` si aucune version n'a encore été publiée.
 */
export async function getSurveyDefinitionForEditing(
  workspaceId: string,
  surveyId: string,
): Promise<SurveyDefinition | null> {
  const { data } = await authedApi.get<{ definitionJson: SurveyDefinition | null }>(
    `/surveys/${encodeURIComponent(surveyId)}/versions/current`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data.definitionJson ?? null;
}

// ---------- AIPD / DPIA (RGPD art. 35) ------------------------------------

/** Contenu d'une AIPD (4 piliers CNIL + signataire). */
export interface AipdContent {
  processingDescription: string;
  necessityProportionality: string;
  risksAndMeasures: string;
  securityMeasures: string;
  signatoryName: string;
  signatoryRole: string;
  /** E-mail du compte connecté au moment de la signature (dérivé serveur). */
  signatoryEmail?: string;
  attestedAt?: string;
}

export interface DpiaStatus {
  signed: boolean;
  version?: string;
  signedAtDay?: string;
  signedByEmail?: string;
  content?: AipdContent;
}

// `signatoryEmail` est dérivé côté serveur (compte connecté) — il NE fait pas
// partie du corps envoyé (le DTO backend rejette les champs inconnus).
export type SignDpiaInput = Omit<AipdContent, 'attestedAt' | 'signatoryEmail'> & {
  attestation: boolean;
};

/** État de l'AIPD d'une enquête (signée ? + contenu pour pré-remplir le formulaire). */
export async function getDpiaStatus(workspaceId: string, surveyId: string): Promise<DpiaStatus> {
  const { data } = await authedApi.get<DpiaStatus>(
    `/surveys/${encodeURIComponent(surveyId)}/dpia`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Signe (ou re-signe) l'AIPD. Lève le gating de diffusion sensible côté backend. */
export async function signDpia(
  workspaceId: string,
  surveyId: string,
  input: SignDpiaInput,
): Promise<{ version: string; signedAtDay: string }> {
  const { data } = await authedApi.post<{ version: string; signedAtDay: string }>(
    `/surveys/${encodeURIComponent(surveyId)}/dpia`,
    input,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

export async function patchSurvey(
  workspaceId: string,
  surveyId: string,
  patch: Partial<Pick<CreateSurveyInput, 'title' | 'identityMode' | 'identitySchema' | 'retentionDays'>>,
): Promise<SurveyFull> {
  const { data } = await authedApi.patch<SurveyFull>(
    `/surveys/${encodeURIComponent(surveyId)}`,
    patch,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/**
 * Supprime une enquête.
 *  - `force=false` (défaut) : le backend refuse si des réponses existent.
 *  - `force=true` : suppression IRRÉVERSIBLE en cascade (réponses art. 9
 *    chiffrées incluses). À n'appeler qu'après sur-validation côté UI.
 */
export async function deleteSurvey(
  workspaceId: string,
  surveyId: string,
  opts: { force?: boolean } = {},
): Promise<{ deleted: true; deletedResponses: number }> {
  const { data } = await authedApi.delete<{ deleted: true; deletedResponses: number }>(
    `/surveys/${encodeURIComponent(surveyId)}${opts.force ? '?force=true' : ''}`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Archive une enquête (soft, réversible). */
export async function archiveSurvey(
  workspaceId: string,
  surveyId: string,
): Promise<{ id: string; status: SurveySummary['status'] }> {
  const { data } = await authedApi.post<{ id: string; status: SurveySummary['status'] }>(
    `/surveys/${encodeURIComponent(surveyId)}/archive`,
    {},
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Désarchive une enquête (repasse en `closed`). */
export async function unarchiveSurvey(
  workspaceId: string,
  surveyId: string,
): Promise<{ id: string; status: SurveySummary['status'] }> {
  const { data } = await authedApi.post<{ id: string; status: SurveySummary['status'] }>(
    `/surveys/${encodeURIComponent(surveyId)}/unarchive`,
    {},
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Publie une nouvelle version de la définition (immuable une fois posée). */
export async function publishDefinition(
  workspaceId: string,
  surveyId: string,
  definitionJson: SurveyDefinition,
): Promise<{ id: string; version: number }> {
  const { data } = await authedApi.post<{ id: string; version: number }>(
    `/surveys/${encodeURIComponent(surveyId)}/versions`,
    { definitionJson },
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

export interface DiffusionResult {
  publicUrl?: string;
  slug: string;
  status: 'draft' | 'published' | 'closed' | 'archived';
}

export async function openDiffusion(workspaceId: string, surveyId: string): Promise<DiffusionResult> {
  const { data } = await authedApi.post<DiffusionResult>(
    `/surveys/${encodeURIComponent(surveyId)}/diffusion`,
    { action: 'open' },
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

export async function closeDiffusion(workspaceId: string, surveyId: string): Promise<DiffusionResult> {
  const { data } = await authedApi.post<DiffusionResult>(
    `/surveys/${encodeURIComponent(surveyId)}/diffusion`,
    { action: 'close' },
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

export interface AggregateRow {
  itemName: string;
  /** Map valeur → count ; valeurs sous k-anonymat (k<5) sont remplacées par null + masked=true. */
  counts: Record<string, number | null>;
  masked: boolean;
}

export interface ResponsesAggregate {
  total: number;
  /** Plancher k-anonymat appliqué. */
  k: number;
  rows: AggregateRow[];
}

export async function getResponsesAggregate(
  workspaceId: string,
  slug: string,
): Promise<ResponsesAggregate> {
  const { data } = await authedApi.get<ResponsesAggregate>(
    `/surveys/${encodeURIComponent(slug)}/aggregate`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

// ---------- Vue par répondant identifiée (Phase B10) ----------

export interface IdentifiedAnswer {
  itemName: string;
  questionLabel: string;
  value: string;
}
export interface IdentifiedResponseRow {
  /** Référence d'affichage (R-1, R-2…) — pas l'identifiant interne. */
  ref: string;
  completed: boolean;
  submittedAtDay: string | null;
  identity: Record<string, string | number>;
  answers: IdentifiedAnswer[];
}
export interface IdentifiedResponses {
  surveyTitle: string;
  slug: string;
  identityMode: 'anonymous' | 'optional' | 'required';
  total: number;
  rows: IdentifiedResponseRow[];
}

/**
 * Vue par répondant IDENTIFIÉE — enquêtes non-anonymes uniquement.
 * À n'appeler que sur action explicite : le backend journalise chaque accès
 * (données identifiées art. 9). Renvoie 400 si l'enquête est anonymous.
 */
export async function getIdentifiedResponses(
  workspaceId: string,
  slug: string,
): Promise<IdentifiedResponses> {
  const { data } = await authedApi.get<IdentifiedResponses>(
    `/surveys/${encodeURIComponent(slug)}/identified`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/**
 * Télécharge l'export d'une enquête (JSON avec codebook, ou CSV). Réservé owner.
 * Le fichier contient des données art. 9 → l'appelant a été averti côté UI.
 * `surveyIdOrSlug` : l'endpoint résout par id ou slug.
 */
export async function downloadExport(
  workspaceId: string,
  surveyIdOrSlug: string,
  format: 'json' | 'csv',
): Promise<void> {
  const res = await authedApi.get(
    `/surveys/${encodeURIComponent(surveyIdOrSlug)}/export?format=${format}`,
    { headers: { 'X-Workspace-Id': workspaceId }, responseType: 'blob' },
  );
  const blob = res.data as Blob;
  // Nom de fichier depuis Content-Disposition si présent.
  const cd = String(res.headers['content-disposition'] ?? '');
  const m = cd.match(/filename="?([^"]+)"?/);
  const filename = m?.[1] ?? `export-${surveyIdOrSlug}.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Utilitaire — extrait un message d'erreur lisible pour l'UI.
 *
 * Le backend renvoie `{ error: { code, message } }` via DomainErrorFilter
 * (API_SPEC §10). On DOIT extraire .message (string) — sinon React error #31
 * quand l'objet est rendu directement comme child JSX.
 */
export function getErrorMessage(err: unknown, fallback = 'Une erreur est survenue.'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; error?: string | { code?: string; message?: string } }
      | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
      return data.error.message;
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

// ===========================================================================
// MCP / OAuth 2.1 (MCP-P1.4) — page Connecteurs IA + écran de consent OAuth.
// ===========================================================================

/** Une autorisation MCP active. */
export interface McpAuthorization {
  id: string;
  client_name: string;
  scopes: string[];
  created_at_day: string;
  last_used_at_day: string | null;
}

/** Liste les autorisations MCP actives du créateur connecté. */
export async function listMcpAuthorizations(workspaceId: string): Promise<McpAuthorization[]> {
  const { data } = await authedApi.get<McpAuthorization[]>(
    '/oauth/authorizations',
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Révocation d'une autorisation MCP par son id (idempotent). */
export async function revokeMcpAuthorization(workspaceId: string, id: string): Promise<void> {
  await authedApi.post(
    `/oauth/authorizations/${encodeURIComponent(id)}/revoke`,
    {},
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
}

/** Contexte renvoyé par GET /oauth/authorize (avant le consent screen). */
export interface OAuthAuthorizeContext {
  client_id: string;
  client_name: string;
  redirect_uri: string;
  requested_scopes: string[];
  grantable_scopes: string[];
  state: string;
  code_challenge: string;
  code_challenge_method: string;
}

/** Récupère le contexte OAuth pour afficher l'écran de consent. */
export async function getOauthAuthorizeContext(
  workspaceId: string,
  params: URLSearchParams,
): Promise<OAuthAuthorizeContext> {
  const { data } = await authedApi.get<OAuthAuthorizeContext>(
    `/oauth/authorize?${params.toString()}`,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Soumet la décision de consent (allow/deny) et renvoie l'URL de redirection. */
export async function submitOauthConsent(
  workspaceId: string,
  body: {
    client_id: string;
    redirect_uri: string;
    state: string;
    code_challenge: string;
    code_challenge_method: string;
    scope: string;
    decision: 'allow' | 'deny';
  },
): Promise<{ redirect_to: string }> {
  const { data } = await authedApi.post<{ redirect_to: string }>(
    '/oauth/authorize/consent',
    body,
    { headers: { 'X-Workspace-Id': workspaceId } },
  );
  return data;
}

/** Configuration optionnelle de surcharge pour les tests (jamais utilisé en prod). */
export function _setBaseURLForTests(url: string, config?: AxiosRequestConfig): void {
  publicApi.defaults.baseURL = url;
  authedApi.defaults.baseURL = url;
  if (config) {
    Object.assign(publicApi.defaults, config);
    Object.assign(authedApi.defaults, config);
  }
}
